import React from "react";
import Image from "next/image";
import Link from "next/link";
import renderOembed from "@/lib/iframeFunction";
import { storeEditorialFields, resolveStoreHtml, resolveStoreName, type StoreEditorial } from "@/lib/storeEditorial";
import { getStoreLogoAlt } from "@/lib/storeLogo";
type EditorialPlacement = "sidebar" | "main" | "main-column" | "faqs" | "full-content";
type RelatedStore = { _id: string; name: string; slug: string; image: string };

export default function StoreEditorialContent({
  store,
  placement = "main",
  relatedStores = [],
}: {
  store: StoreEditorial & { name: string; slug: string; image?: string; content?: string };
  placement?: EditorialPlacement;
  relatedStores?: RelatedStore[];
}) {
  const sidebarKeys = new Set(["sidebarContent", "whyTrustUs", "waysToSave"]);
  const isMainPlacement = placement === "main" || placement === "main-column";
  const sections = storeEditorialFields.filter(({ key }) => {
    const hasContent = store[key]?.replace(/<[^>]*>/g, "").trim();
    return Boolean(hasContent) && (placement === "sidebar" ? sidebarKeys.has(key) : !sidebarKeys.has(key));
  });
  const faqs = (store.faqs || []).filter(item => item.question.trim() && item.answer.trim());
  const mainSections = sections.filter(({ key }) => !sidebarKeys.has(key));
  const showMainContent = isMainPlacement && (Boolean(store.featuredContent) || Boolean(store.content) || mainSections.length > 0 || (placement === "main" && faqs.length > 0));

  if (placement === "full-content") {
    if (!store.content?.trim()) return null;

    return (
      <section className="w-full border-t border-gray-200 pt-8">
        <div className="prose max-w-none text-gray-800" dangerouslySetInnerHTML={{ __html: renderOembed(resolveStoreHtml(store.content, store.name)) }} />
      </section>
    );
  }

  if (placement === "faqs") {
    if (!faqs.length) return null;

    return (
      <section className="w-full border-t border-gray-200 pt-8">
        <h2 className="mb-5 text-2xl font-bold text-gray-900">{store.name} frequently asked questions</h2>
        <div className="divide-y divide-gray-200 border-y border-gray-200">
          {faqs.map((item, index) => (
            <details key={index} className="group py-4">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium text-gray-900">
                {resolveStoreName(item.question, store.name)}
                <span className="text-xl text-gray-500 transition group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-gray-600">{resolveStoreName(item.answer, store.name)}</p>
            </details>
          ))}
        </div>
      </section>
    );
  }

  if (!sections.length && !showMainContent) return null;

  if (isMainPlacement) {
    return (
      <div className="mt-10 w-full max-w-none space-y-10 border-t border-gray-200 pt-10">
        {store.featuredContent && (
          <section className="grid w-full gap-8 rounded-2xl border border-gray-200 bg-white p-6 shadow-[0_8px_24px_rgba(15,23,42,0.06)] md:grid-cols-[minmax(0,1fr)_260px] md:p-10">
            <div className="min-w-0">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-900">What to expect from</p>
              <h2 className="mb-8 text-3xl font-extrabold leading-tight text-purple-700 md:text-5xl">{store.name} Coupon Codes</h2>
              <div className="prose max-w-none text-gray-800" dangerouslySetInnerHTML={{ __html: renderOembed(resolveStoreHtml(store.featuredContent, store.name)) }} />
            </div>
            {store.image && (
              <div className="flex items-start justify-center md:justify-end">
                <Image
                  src={store.image.startsWith("http") ? store.image : `https://www.itscoupons.com${store.image}`}
                  alt={getStoreLogoAlt(store.slug)}
                  width={208}
                  height={208}
                  className="h-52 w-52 object-contain"
                />
              </div>
            )}
          </section>
        )}

        {mainSections.length > 0 && (
          <section className="space-y-6">
            <div className={`grid gap-x-10 gap-y-8 ${placement === "main-column" ? "md:grid-cols-1" : "md:grid-cols-2"}`}>
              {mainSections.map(({ key, label }) => (
                <section key={key} className="border-t border-gray-200 pt-4">
                  {key !== "storeInformation" && (
                    <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-900">
                      {key === "aboutStore" ? `About ${store.name}` : label}
                    </h3>
                  )}
                  <div className="prose prose-sm max-w-none text-gray-600" dangerouslySetInnerHTML={{ __html: renderOembed(resolveStoreHtml(store[key] || "", store.name)) }} />
                </section>
              ))}
            </div>
          </section>
        )}

        {placement === "main" && faqs.length > 0 && (
          <section>
            <h2 className="mb-5 text-2xl font-bold text-gray-900">{store.name} frequently asked questions</h2>
            <div className="divide-y divide-gray-200 border-y border-gray-200">
              {faqs.map((item, index) => (
                <details key={index} className="group py-4">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium text-gray-900">
                    {resolveStoreName(item.question, store.name)}
                    <span className="text-xl text-gray-500 transition group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-3 max-w-3xl text-sm leading-6 text-gray-600">{resolveStoreName(item.answer, store.name)}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {placement === "main" && store.content && (
          <section className="w-full border-t border-gray-200 pt-8">
            <div className="prose mx-auto max-w-none text-gray-800" dangerouslySetInnerHTML={{ __html: renderOembed(resolveStoreHtml(store.content, store.name)) }} />
          </section>
        )}

      </div>
    );
  }

  return (
    <div className={placement === "sidebar" ? "space-y-7" : "mt-10 border-t border-gray-200 pt-10"}>
      <div className={placement === "sidebar" ? "space-y-7" : "space-y-10"}>
        {sections.map(({ key, label }, index) => (
          <React.Fragment key={key}>
            {placement === "sidebar" && relatedStores.length > 0 && key === "whyTrustUs" && !sections.some((section) => section.key === "sidebarContent") && (
              <TopStoresList stores={relatedStores} />
            )}
            <section className={placement === "sidebar" ? "border-t border-gray-200 pt-6 first:border-t-0 first:pt-0" : "prose max-w-none text-gray-800"}>
              {placement === "sidebar" ? (
                <p className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-900">
                  {key === "sidebarContent" ? "About Store" : key === "aboutStore" ? `About ${store.name}` : label}
                </p>
              ) : (
                <h3>{key === "aboutStore" ? `About ${store.name}` : label}</h3>
              )}
              <div className={placement === "sidebar" ? "prose prose-sm max-w-none text-gray-600" : ""} dangerouslySetInnerHTML={{ __html: renderOembed(resolveStoreHtml(store[key] || "", store.name)) }} />
            </section>
            {placement === "sidebar" && relatedStores.length > 0 && key === "sidebarContent" && (
              <TopStoresList stores={relatedStores} />
            )}
            {placement === "sidebar" && relatedStores.length > 0 && index === sections.length - 1 && !sections.some((section) => section.key === "sidebarContent" || section.key === "whyTrustUs") && (
              <TopStoresList stores={relatedStores} />
            )}
          </React.Fragment>
        ))}
        {showMainContent && store.content && (
          <section className="prose max-w-none text-gray-800" dangerouslySetInnerHTML={{ __html: renderOembed(resolveStoreHtml(store.content, store.name)) }} />
        )}
        {showMainContent && faqs.length > 0 && (
          <section>
            <h2 className="mb-5 text-2xl font-bold text-gray-900">{store.name} frequently asked questions</h2>
            <div className="divide-y divide-gray-200 border-y border-gray-200">
              {faqs.map((item, index) => (
                <details key={index} className="group py-4">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium text-gray-900">
                    {resolveStoreName(item.question, store.name)}
                    <span className="text-xl text-gray-500 transition group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-3 max-w-3xl text-sm leading-6 text-gray-600">{resolveStoreName(item.answer, store.name)}</p>
                </details>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function TopStoresList({ stores }: { stores: RelatedStore[] }) {
  return (
    <section className="border-t border-gray-200 pt-6">
      <p className="mb-4 text-xs font-bold uppercase tracking-wide text-gray-900">Top Stores</p>
      <div className="grid grid-cols-3 gap-x-2 gap-y-4">
        {stores.slice(0, 9).map((store) => (
          <Link
            key={store._id}
            href={`/stores/${store.slug}`}
            title={store.name}
            className="flex min-w-0 flex-col items-center gap-1.5 rounded-lg p-1 text-center transition hover:bg-purple-50"
          >
            <span className="relative h-10 w-10 overflow-hidden rounded-full border border-gray-200 bg-white">
              <Image
                src={store.image.startsWith("http") ? store.image : `https://www.itscoupons.com${store.image.startsWith("/") ? store.image : `/${store.image}`}`}
                alt={getStoreLogoAlt(store.slug)}
                width={40}
                height={40}
                sizes="40px"
                className="h-full w-full object-cover"
                style={{ width: "100%", height: "100%" }}
              />
            </span>
            <span className="w-full truncate text-[11px] font-medium leading-tight text-gray-700">{store.name}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}
